/**
 * 12-step OKLCH ramps (Radix semantics: 1-2 app backgrounds, 3-5 component
 * fills, 6-8 borders, 9-10 solids, 11-12 text). Every ramp shares one
 * lightness curve per mode (the Stripe/Linear method) so "N steps apart"
 * contrast holds across hues. Brand ramps pin the logo colours as exact
 * anchors; steps between two anchors are interpolated so lightness stays
 * monotonic.
 */
import { hexToOklch, oklchToHex, type Oklch } from './color.js';

export type Mode = 'dark' | 'light';
export type Step = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
export const STEPS: readonly Step[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

/** Shared lightness curves (index 0 = step 1). */
export const LIGHTNESS: Record<Mode, readonly number[]> = {
  light: [0.99, 0.975, 0.95, 0.925, 0.895, 0.86, 0.805, 0.73, 0.6, 0.555, 0.48, 0.255],
  dark: [0.178, 0.205, 0.245, 0.278, 0.31, 0.35, 0.41, 0.495, 0.62, 0.67, 0.79, 0.935],
};

/** Chroma multipliers per step, relative to a ramp's peak chroma. */
export const CHROMA_CURVE: Record<Mode, readonly number[]> = {
  light: [0.04, 0.09, 0.18, 0.28, 0.38, 0.48, 0.6, 0.78, 1, 0.96, 0.85, 0.55],
  dark: [0.12, 0.16, 0.26, 0.34, 0.42, 0.5, 0.6, 0.75, 1, 0.96, 0.8, 0.35],
};

export interface RampSpec {
  name: string;
  /** OKLCH hue in degrees. */
  hue: number;
  /** Peak chroma (reached at step 9 before gamut mapping). */
  chroma: number;
  /** Exact colours pinned at given steps, per mode. */
  anchors?: Partial<Record<Mode, Partial<Record<Step, string>>>>;
  /** Overrides the shared lightness curve (rare: neutrals tuned to the v0 surfaces). */
  lightness?: Partial<Record<Mode, readonly number[]>>;
}

export type Ramp = readonly string[];

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpHue(a: number, b: number, t: number): number {
  let d = b - a;
  if (d > 180) d -= 360;
  if (d < -180) d += 360;
  return (a + d * t + 360) % 360;
}

/** Generates the 12 hex steps of a ramp for one mode. */
export function generateRamp(spec: RampSpec, mode: Mode): Ramp {
  const curve = spec.lightness?.[mode] ?? LIGHTNESS[mode];
  const chromaCurve = CHROMA_CURVE[mode];
  const anchors = spec.anchors?.[mode] ?? {};
  const anchorSteps = (Object.keys(anchors).map(Number) as Step[]).sort((a, b) => a - b);
  const anchorLch = new Map<Step, Oklch>(
    anchorSteps.map((s) => [s, hexToOklch(anchors[s] as string)]),
  );

  return STEPS.map((step) => {
    const pinned = anchors[step];
    if (pinned) return pinned.toLowerCase();
    const before = [...anchorSteps].reverse().find((s) => s < step);
    const after = anchorSteps.find((s) => s > step);
    if (before !== undefined && after !== undefined) {
      const a = anchorLch.get(before) as Oklch;
      const b = anchorLch.get(after) as Oklch;
      const t = (step - before) / (after - before);
      return oklchToHex({ l: lerp(a.l, b.l, t), c: lerp(a.c, b.c, t), h: lerpHue(a.h, b.h, t) });
    }
    const nine = anchorLch.get(9);
    if (step === 10 && nine) {
      // Radix semantics: step 10 is the hover of solid step 9, one notch away.
      return oklchToHex({ ...nine, l: nine.l + (mode === 'dark' ? 0.05 : -0.04) });
    }
    const i = step - 1;
    return oklchToHex({
      l: curve[i] as number,
      c: spec.chroma * (chromaCurve[i] as number),
      h: spec.hue,
    });
  });
}

/** Brand and status ramp inputs. Logo colours are v0 values eyeballed from the raster. */
export const LOGO = {
  ink: '#0a1a3a',
  navy: '#1c3f7a',
  orangeLight: '#fbb040',
  orange: '#f7941d',
  orangeDeep: '#e8650a',
  orangeText: '#b4470a',
} as const;

export const RAMP_SPECS: readonly RampSpec[] = [
  {
    name: 'navy',
    hue: 262,
    chroma: 0.11,
    anchors: {
      light: { 9: LOGO.navy, 10: '#163266', 11: '#12295a', 12: LOGO.ink },
      dark: { 9: LOGO.navy },
    },
  },
  {
    name: 'orange',
    hue: 58,
    chroma: 0.18,
    anchors: {
      light: { 8: LOGO.orange, 9: LOGO.orangeDeep, 11: LOGO.orangeText },
      dark: { 9: LOGO.orange, 10: LOGO.orangeLight },
    },
    // The logo orange is intrinsically light, so dark-mode text steps sit higher.
    lightness: {
      dark: [0.178, 0.205, 0.245, 0.278, 0.31, 0.35, 0.41, 0.495, 0.62, 0.67, 0.86, 0.95],
    },
  },
  { name: 'charcoal', hue: 262, chroma: 0.032 },
  { name: 'red', hue: 20, chroma: 0.2 },
  { name: 'amber', hue: 75, chroma: 0.15 },
  { name: 'green', hue: 160, chroma: 0.15 },
  { name: 'blue', hue: 255, chroma: 0.16 },
  { name: 'violet', hue: 300, chroma: 0.16 },
];

export function generateRamps(mode: Mode): Record<string, Ramp> {
  return Object.fromEntries(RAMP_SPECS.map((spec) => [spec.name, generateRamp(spec, mode)]));
}
